import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, getUserId } from '../../utils/api';
import { useChatPlatform } from '../context/ChatPlatformContext';
import SidebarHeader from './SidebarHeader';
import SidebarMenuButtons from './SidebarMenuButtons';
import SessionList from './SessionList';

function SidebarFooter({ setIsUserMenuOpen, membershipLabel }) {
    const { currentUser, isSidebarOpen } = useChatPlatform();
    const navigate = useNavigate();
    const userId = getUserId();

    return (
        <div
            className={`flex items-center cursor-pointer rounded-md ${isSidebarOpen ? 'gap-3 p-2 hover:bg-[#0000000c]' : 'w-9 h-9 justify-center hover:bg-app-hover'}`}
            onClick={() => {
                if (isAuthenticated()) {
                    setIsUserMenuOpen((v) => !v);
                } else {
                    navigate('/login');
                }
            }}
        >
            {currentUser?.imageUrl ? (
                <img
                    src={currentUser.imageUrl}
                    alt="avatar"
                    className="w-6 h-6 min-w-6 min-h-6 rounded-full object-cover flex-shrink-0"
                    onError={(e) => { e.currentTarget.src = 'https://static.simplize.vn/static/fe/imgs/logo-black.png'; }}
                />
            ) : (
                <div className="w-6 h-6 min-w-6 min-h-6 rounded-full bg-pink-500 flex items-center justify-center font-bold text-white flex-shrink-0">
                    {userId.substring(5, 7).toUpperCase()}
                </div>
            )}
            {isSidebarOpen && (
                <div className="flex-1 overflow-hidden">
                    <span className="font-medium text-sm block truncate whitespace-nowrap">{currentUser?.fullName || 'Anonymous'}</span>
                    <p className="text-xs text-gray-500 whitespace-nowrap">{membershipLabel}</p>
                </div>
            )}
        </div>
    );
}

export default function Sidebar({
    handleNewChat,
    isLoading,
    isInputDisabled,
    showNotificationPanel,
    setShowNotificationPanel,
    notificationCount,
    setNotificationCount,
    isInitializing,
    editingSessionId,
    editingName,
    setEditingName,
    openMenuSessionId,
    setOpenMenuSessionId,
    switchSession,
    startEditing,
    handleRenameSession,
    handleDeleteSession,
    setIsUserMenuOpen,
    membershipLabel,
}) {
    const { isMobile, isSidebarOpen, setIsSidebarOpen } = useChatPlatform();

    if (!isAuthenticated()) return null;

    return (
        <>
            <div
                className={`flex flex-col bg-app-sidebar text-black overflow-hidden ${
                    isMobile
                        ? `fixed top-0 left-0 h-full z-[60] w-64 transform will-change-transform transition-transform duration-200 ease-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
                        : `transition-[width] duration-200 ease-in-out ${isSidebarOpen ? 'w-64' : 'w-[52px]'}`
                }`}
                style={!isMobile ? { borderRight: '1px solid #0d0d0d0d' } : {}}
            >
                <SidebarHeader />

                <SidebarMenuButtons
                    handleNewChat={handleNewChat}
                    isLoading={isLoading}
                    isInputDisabled={isInputDisabled}
                    showNotificationPanel={showNotificationPanel}
                    setShowNotificationPanel={setShowNotificationPanel}
                    notificationCount={notificationCount}
                    setNotificationCount={setNotificationCount}
                    switchSession={switchSession}
                />

                <SessionList
                    isInitializing={isInitializing}
                    editingSessionId={editingSessionId}
                    editingName={editingName}
                    setEditingName={setEditingName}
                    openMenuSessionId={openMenuSessionId}
                    setOpenMenuSessionId={setOpenMenuSessionId}
                    switchSession={switchSession}
                    startEditing={startEditing}
                    handleRenameSession={handleRenameSession}
                    handleDeleteSession={handleDeleteSession}
                />

                {!isSidebarOpen && !isMobile && <div className="flex-1"></div>}

                <div className={`p-2 ${isMobile && !isSidebarOpen ? 'hidden' : ''} relative`}>
                    <SidebarFooter
                        setIsUserMenuOpen={setIsUserMenuOpen}
                        membershipLabel={membershipLabel}
                    />
                </div>
            </div>

            {isMobile && isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-[55]"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}
        </>
    );
}
